import React from 'react';
import { ChatMessageProps } from '@/types/chat';
import { CurveChart } from '@/components/chat/';
import { ChatTable } from '@/components/chat/';
import { DrainDetection } from '@/components/chat/';
import { ImageCarousel } from '@/components/chat/';
import { ReasoningSteps } from '@/components/chat/';
import { CodeBlock } from '@/components/chat/CodeBlock';
import { cn } from '@/lib/utils';
import { Sparkles, ArrowRight } from 'lucide-react';
import { MessageText } from '@/components/chat/';
import { Feedback } from '@/components/chat/';
import { CSVPreview } from '@/components/chat/';
import { formatFileSize, getFileIcon } from '@/services/attachmentsApi';

export const ChatMessage = ({
  message,
  onComponentAction,
  onSelectNextPrompt,
  conversation_id,
  streamingStatus,
}: ChatMessageProps) => {
  const isUser = message.role === 'user';
  const blocks = message.blocks;
  const useBlocks = blocks && blocks.length > 0;

  const hasReasoningSteps =
    message.reasoningSteps && message.reasoningSteps.length > 0;
  if (
    blocks !== undefined &&
    blocks.length === 0 &&
    (!message.content ||
      (typeof message.content === 'string' && !message.content.trim())) &&
    (!message.uploadedFiles || message.uploadedFiles.length === 0) &&
    !hasReasoningSteps &&
    !streamingStatus
  ) {
    return null;
  }

  const messageTextContent = useBlocks
    ? blocks
        ?.filter(b => b.type === 'text')
        .map(b => b.data)
        .join('\n') ?? ''
    : typeof message.content === 'string'
    ? message.content
    : '';
  const nextPrompts =
    message.transparencySummary?.nextPrompts?.filter(
      prompt => typeof prompt === 'string' && prompt.trim().length > 0,
    ) ?? [];

  const renderTextBlock = (text: string, index: number) => {
    const isStreamingMessage = !message.messageCompleted && !!streamingStatus;
    return (
      <div
        key={`text-${index}`}
        className={cn(
          isUser
            ? 'rounded-2xl px-4 py-3 bg-[#E2E7EB] text-[#252F33] shadow-sm'
            : 'text-[#252F33]',
        )}>
        <div className="break-words text-sm sm:text-base leading-relaxed">
          <MessageText
            text={text}
            isUser={isUser}
            isStreaming={isStreamingMessage}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      {/* Assistant label */}
      {!isUser && (
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-[18px] w-[18px] text-[#252F33]" />
          <span className="text-[15px] font-semibold text-[#161618]">
            Bernoulli
          </span>
        </div>
      )}

      {/* Reasoning steps - shown before response content */}
      {!isUser && (hasReasoningSteps || streamingStatus) && (
        <ReasoningSteps
          steps={message.reasoningSteps || []}
          isComplete={message.messageCompleted}
          streamingStatus={streamingStatus}
          isRestored={message.isRestored}
          restoredDurationSec={message.restoredDurationSec}
          transparencySummary={message.transparencySummary}
        />
      )}

      {/* Message content */}
      <div
        className={cn(
          'flex flex-col gap-1.5',
          isUser ? 'max-w-[85%] items-end' : 'w-full',
        )}>
        {useBlocks ? (
          blocks?.map((block, index) => {
            if (block.type === 'text') {
              return renderTextBlock(block.data, index);
            }

            if (block.type === 'charts') {
              const charts = Array.isArray(block.data)
                ? block.data
                : [block.data];
              return (
                <div
                  key={`charts-${index}`}
                  className={cn(
                    'w-full gap-4',
                    index > 0 && 'mt-2',
                    charts.length === 1
                      ? 'flex flex-col'
                      : 'grid grid-cols-1 md:grid-cols-2',
                  )}>
                  {charts.map((chart: any, idx: number) => (
                    <CurveChart
                      key={idx}
                      config={chart.config}
                      onAction={onComponentAction}
                    />
                  ))}
                </div>
              );
            }

            if (block.type === 'code') {
              return (
                <div key={`code-${index}`} className="w-full">
                  <CodeBlock
                    code={block.data?.script || ''}
                    language={block.data?.language || 'ruby'}
                    description={block.data?.description}
                  />
                </div>
              );
            }

            if (block.type === 'files') {
              const csvFiles = block.data?.filter(
                (file: any) =>
                  file.filename?.toLowerCase().endsWith('.csv') && file.headers,
              );

              if (csvFiles && csvFiles.length > 0) {
                return (
                  <div key={`files-${index}`} className="w-full mt-2">
                    {csvFiles.map((file: any, idx: number) => (
                      <CSVPreview
                        key={idx}
                        files={[]}
                        csvMetadata={{
                          headers: file.headers,
                          sample_rows: file.sample_rows,
                          row_count: file.row_count,
                          filename: file.filename,
                        }}
                      />
                    ))}
                  </div>
                );
              }

              return (
                <div key={`files-${index}`} className="w-full mt-2 space-y-2">
                  {block.data?.map((file: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100">
                        <span className="text-xl">📎</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#252F33] truncate">
                          {file.filename}
                        </div>
                        <div className="text-xs text-[#768D95]">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }

            if (block.type === 'table') {
              return (
                <div key={`table-${index}`} className="w-full mt-2">
                  <ChatTable tableConfig={block.data} />
                </div>
              );
            }

            if (block.type === 'image_carousel') {
              return (
                <div key={`image_carousel-${index}`} className="w-full mt-2">
                  <ImageCarousel images={block.data?.images || []} />
                </div>
              );
            }

            if (block.type === 'drain_detection') {
              return (
                <div key={`drain_detection-${index}`} className="w-full mt-2">
                  <DrainDetection data={block.data} />
                </div>
              );
            }

            return null;
          })
        ) : (
          <>
            {typeof message.content === 'string' && message.content.trim() && (
              <div
                className={cn(
                  isUser
                    ? 'rounded-2xl px-4 py-3 bg-[#E2E7EB] text-[#252F33] shadow-sm'
                    : 'text-[#252F33]',
                )}>
                <div className="break-words text-sm sm:text-base leading-relaxed">
                  <MessageText
                    text={message.content}
                    isUser={isUser}
                    isStreaming={!message.messageCompleted && !!streamingStatus}
                  />
                </div>
              </div>
            )}

            {isUser &&
              message.extras?.files &&
              message.extras.files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {message.extras.files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#DDE3E6] text-[#252F33] text-sm">
                      <span className="text-base" role="img" aria-label="file">
                        {getFileIcon(file.filename)}
                      </span>
                      <span className="max-w-[150px] truncate">
                        {file.filename}
                      </span>
                      <span className="text-[#768D95] text-xs">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

            {message.uploadedFiles && message.uploadedFiles.length > 0 && (
              <CSVPreview
                files={message.uploadedFiles}
                csvMetadata={
                  message.extras?.files?.[0]
                    ? {
                        headers: message.extras.files[0].headers,
                        sample_rows: message.extras.files[0].sample_rows,
                        row_count: message.extras.files[0].row_count,
                        filename: message.extras.files[0].filename,
                      }
                    : undefined
                }
              />
            )}
          </>
        )}
      </div>

      {/* Feedback actions (copy, thumbs up, thumbs down) */}
      {!isUser && message.messageCompleted && (
        <Feedback
          conversation_id={conversation_id}
          request_id={message.request_id}
          existingFeedback={message.existingFeedback}
          messageContent={messageTextContent}
        />
      )}

      {!isUser && message.messageCompleted && nextPrompts.length > 0 && (
        <div className="w-full mt-4 pt-4 border-t border-slate-200">
          <p className="text-sm font-medium text-[#768D95]">
            Suggested follow-ups
          </p>
          <div className="mt-2">
            {nextPrompts.map((prompt, index) => (
              <button
                key={`${prompt}-${index}`}
                type="button"
                onClick={() => onSelectNextPrompt?.(prompt)}
                className="w-full flex items-center justify-between gap-2 py-2 text-left text-sm text-[#252F33] hover:text-[#007CA0] transition-colors">
                <span className="flex-1">{prompt}</span>
                <ArrowRight className="h-4 w-4 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
